<template>
  <div>
    <v-container class="pt-3 pb-6 px-6">

      <!-- Floating Button -->
      <floating-button @click="save()" save :disabled="invalid" v-if="authUser"/>

      <!-- Page Header -->
      <page-header
        :title="isNew + ' Orden de Trabajo'"
        icon="mdi-file-document-edit"
        divider="-"
        :breadcrumbs="breadcrumbs.items"
        refresh
        v-on:refresh="getOrder()"
      ></page-header>

      <v-row align="center" justify="space-around">
        <v-col cols="12">
          <v-card>
            <!-- Loading Bar -->
            <v-progress-linear
              indeterminate
              v-if="isLoadingData"
            ></v-progress-linear>
            <!-- Content -->
            <v-card-text v-if="authUser">
              <v-stepper v-model="step" vertical class="pa-0 elevation-0">
                
                <!-- Create -->
                <v-stepper-step step="1"
                  :rules="[() => !($v.$invalid && $v.$dirty)]"
                  :editable="!!$route.params.id" 
                  :complete="!$route.params.id && step === 2"
                >Orden de trabajo</v-stepper-step>
                <v-stepper-content step="1">
                  <v-container>
                    <v-row align="center">
                      <!-- Code -->
                      <v-col cols="3">
                        <v-text-field
                          v-model="work_order.code"
                          :error-messages="codeErrors"
                          label="Código OT"
                          counter="20"
                          maxlength="20"
                          outlined
                          @blur="$v.work_order.code.$touch()"
                        ></v-text-field>
                      </v-col>
                      <!-- Type -->
                      <v-col cols="3">
                        <v-select
                          v-model="work_order.work_type_id"
                          :error-messages="typeErrors"
                          :items="workTypes"
                          label="Tipo de trabajo"
                          outlined
                          @blur="$v.work_order.work_type_id.$touch()"
                        ></v-select>
                      </v-col>
                      <!-- Client -->
                      <v-col cols="3">
                        <v-select
                          v-model="client"
                          :error-messages="clientErrors"
                          :items="clients"
                          :item-text="clientText"
                          item-value="id"
                          label="Cliente"
                          outlined
                          @change="$v.client.$touch()"
                          @blur="$v.client.$touch()"
                        ></v-select>
                      </v-col>
                      <!-- State -->
                      <v-col cols="3">
                        <v-select
                          v-model="work_order.state"
                          :error-messages="stateErrors"
                          :items="stateItems"
                          label="Estado"
                          outlined
                          @blur="$v.work_order.state.$touch()"
                        >
                          <template v-slot:selection="data">
                            <v-chip
                              style="cursor: pointer"
                              :class="data.item === 'PENDIENTE' ? '' : 'white--text'"
                              :color="data.item === 'EN CURSO' ? 'primary' : data.item === 'FINALIZADA' ? 'deep-orange' : ''"
                            >{{ data.item}}</v-chip>
                          </template>
                        </v-select>
                      </v-col>
                      <!-- Contract Award -->
                      <v-col cols="3">
                        <v-select
                          v-model="work_order.contract_award_id"
                          :error-messages="contractErrors"
                          :items="contracts"
                          :disabled="contracts.length === 0"
                          item-text="client_code"
                          item-value="id"
                          label="Adjudicación"
                          outlined
                          @blur="$v.work_order.contract_award_id.$touch()"
                        ></v-select>
                      </v-col>
                      <!-- Farms -->
                      <v-col cols="4">
                        <v-autocomplete
                          v-model="work_order.wind_farms"
                          :error-messages="farmsErrors"
                          :items="farms"
                          :disabled="!work_order.contract_award_id"
                          item-text="name"
                          item-value="id"
                          label="Parques"
                          chips
                          deletable-chips
                          outlined
                          multiple
                          @blur="$v.work_order.wind_farms.$touch()"
                        ></v-autocomplete>
                      </v-col>
                      <!-- Technicians -->
                      <v-col cols="5">
                        <v-autocomplete
                          v-model="work_order.technicians"
                          :error-messages="techniciansErrors"
                          :items="technicians"
                          label="Operarios"
                          :item-text="technicianText"
                          item-value="id"
                          chips
                          deletable-chips
                          outlined
                          multiple
                          @blur="$v.work_order.technicians.$touch()"
                        ></v-autocomplete>
                      </v-col>
                      <!-- Summary -->
                      <v-col cols="12">
                        <v-textarea
                          v-model="work_order.summary"
                          :error-messages="summaryErrors"
                          label="Descripción"
                          counter
                          outlined
                          @blur="$v.work_order.summary.$touch()"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-stepper-content>

                <!-- Files -->
                <v-stepper-step step="2" :editable="!!$route.params.id">Subir archivos</v-stepper-step>
                <v-stepper-content step="2">
                  <v-container class="my-0">
                    <image-upload
                      :items="work_order.file_attachments"
                      :loading="isLoadingFiles"
                      acceptAllFiles
                      hideHeader
                      hideEditor
                      title="Archivos subidos"
                      typeTitle="Archivo"
                      @save="uploadFile($event)"
                      @delete="deleteFile($event)"/>
                  </v-container>
                </v-stepper-content>
                <!-- Interventions -->

                <v-stepper-step step="3" :editable="!!$route.params.id">Intervenciones</v-stepper-step>
                <v-stepper-content step="3">
                  <work-order-interventions />
                </v-stepper-content>

              </v-stepper>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

    </v-container>
  </div>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import PageHeader from "@/components/PageHeader";
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { WorkOrdersRepository } from "@/api/Repositories";
import Util from "@/util/index";
import STATES from '@/models/States'
import ImageUpload from '@/components/ImageUpload'
import WorkOrderInterventions from '@/components/workOrders/WorkOrderInterventions'

export default {
  components: {
    FloatingButton,
    PageHeader,
    ImageUpload,
    WorkOrderInterventions
  },
  mixins: [validationMixin],
  validations: {
    work_order: {
      code: { required },
      work_type_id: { required },
      state: { required },
      wind_farms: { required },
      contract_award_id: { required },
      technicians: { required },
      summary: { required }
    },
    client: { required }
  },
  data: () => ({
    newOrderId: null,
    authUser: false,
    step: 1,
    isLoadingFiles: false,
    isLoadingData: false,
    client: null,
    work_order: {},
    oldCode: '',
    alert: true,
    stateItems: STATES.WORK_ORDER_STATE,
    workTypes: STATES.WORK_ORDER_TYPES,
    fileHeaders: [
      { text: "Nombre", value: "name", align: "center" },
      { text: "Acción", value: "action", sortable: false, align: "center" }
    ],
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Orden de Trabajo", disabled: true }
      ]
    },
  }),
  computed: {
    loading: {
      get () {
        return this.$store.state.loading
      },
      set (val) {
        this.$store.commit('loading', val)
      }
    },
    isNew () {
      return this.$route.params.id ? 'Modificar' : 'Añadir'
    },
    invalid () {
      return this.$v.$invalid && this.$v.$dirty
    },
    clients () {
      return this.$store.state.clients.clients
    },
    contracts () {
      if (this.client) {
        return this.$store.state.contracts.contracts.filter(element => {
          return element.state !== 'FINALIZADA' && element.client_id === this.client
        })
      } else {
        return []
      }
    },
    farms () {
      if (this.client) {
        let farms = []
        this.clients.filter(element => {
          if (element.id === this.client) farms = element.wind_farms
        })
        return farms
      } else {
        return null
      }
    },
    technicians () {
      return this.$store.state.users.all.filter(element => {
        return element.roles.filter(e => e.name === 'Operario').length > 0 && element.isActive === true
      })
    },
    codeErrors () {
      const errors = []
      if (!this.$v.work_order.code.$dirty) return errors
      !this.$v.work_order.code.required && errors.push('Obligatorio.')
      return errors
    },
    typeErrors () {
      const errors = []
      if (!this.$v.work_order.work_type_id.$dirty) return errors
      !this.$v.work_order.work_type_id.required && errors.push('Obligatorio.')
      return errors
    },
    stateErrors () {
      const errors = []
      if (!this.$v.work_order.state.$dirty) return errors
      !this.$v.work_order.state.required && errors.push('Obligatorio.')
      return errors
    },
    techniciansErrors () {
      const errors = []
      if (!this.$v.work_order.technicians.$dirty) return errors
      !this.$v.work_order.technicians.required && errors.push('Obligatorio.')
      return errors
    },
    clientErrors () {
      const errors = []
      if (!this.$v.client.$dirty) return errors
      !this.$v.client.required && errors.push('Obligatorio.')
      this.client && this.contracts.length === 0 && errors.push('Este cliente no tiene adjudicaciones.')
      return errors
    },
    contractErrors () {
      const errors = []
      if (!this.$v.work_order.contract_award_id.$dirty) return errors
      !this.$v.work_order.contract_award_id.required && errors.push('Obligatorio.')
      return errors
    },
    farmsErrors () {
      const errors = []
      if (!this.$v.work_order.wind_farms.$dirty) return errors
      !this.$v.work_order.wind_farms.required && errors.push('Obligatorio.')
      return errors
    },
    summaryErrors () {
      const errors = []
      if (!this.$v.work_order.summary.$dirty) return errors
      !this.$v.work_order.summary.required && errors.push('Obligatorio.')
      return errors
    },
    fileErrors () {
      const errors = []
      !this.fileSizeRule && errors.push('El tamaño del archivo debe ser menor a 20 MB.')
      return errors
    },
    fileSizeRule () {
      if (this.file) {
        return this.file.size < 20000000
      } else {
        return true
      }
    }
  },
  methods: {
    technicianText (item) {
      if (item.lastName2) {
        return item.firstName + ' ' + item.lastName1 + ' ' + item.lastName2
      } else {
        return item.firstName + ' ' + item.lastName1
      }
    },
    clientText (item) {
        return item.brand_name + ' - ' + item.business_name
    },
    deleteFile (file) {
      this.isLoadingFiles = true
      this.$store.dispatch('orders/deleteFile', file)
        .then(() => {
          this.$toast("Archivo borrado correctamente.", {
            color: "success",
            x: "center",
            y: "top",
            icon: "mdi-information"
          });
          this.loadFiles()
        })
        .catch(error => {
          var errorToastMessage = "Ha ocurrido un error borrando el archivo."
          Util.showErrorToast(this, error, errorToastMessage);
        })
        .finally(() => {
          this.isLoadingFiles = false
        })
    },
    uploadFile (file) {
      this.loading = true
      const upload = {
        id: this.$route.params.id,
        fileToUpload: file
      }
      this.$store.dispatch('orders/uploadFile', upload)
        .then(() => {
          this.$toast("Archivo subido correctamente.", {
            color: "success",
            x: "center",
            y: "top",
            icon: "mdi-information"
          });
          this.file = null
          this.loadFiles()
        })
        .catch(error => {
          var errorToastMessage = "Ha ocurrido un error subiendo el archivo."
          Util.showErrorToast(this, error, errorToastMessage);
        })
        .finally(() => {
          this.loading = false
          this.$store.commit('uploading', null)
        })
    },
    loadFiles () {
      const id = this.$route.params.id ? this.$route.params.id : this.newOrderId
      this.isLoadingFiles = true
      const body = {
        id: id,
        with: 'fileAttachments'
      }
      WorkOrdersRepository.get(body)
        .then(response => {
          let tempOrder = response.data.data

          this.work_order.file_attachments = tempOrder.file_attachments
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando los archivos."
            Util.showErrorToast(this, error, errorToastMessage);
          }
      })
      .finally(() => {
        this.isLoadingFiles = false
      })
    },
    getOrder (work_order_id) {
      this.isLoadingData = true
      const id = work_order_id ? work_order_id : this.$route.params.id
      const body = {
        id: id,
        with: 'technicians,windFarms,contractAward.client,fileAttachments'
      }
      if (id) {
        WorkOrdersRepository.get(body)
          .then(response => {
            let tempOrder = response.data.data
            if (tempOrder.technicians) tempOrder.technicians = tempOrder.technicians.map(r => r.id);
            if (tempOrder.wind_farms) tempOrder.wind_farms = tempOrder.wind_farms.map(r => r.id);

            this.work_order = tempOrder
            console.log("getOrder -> tempOrder", tempOrder)
            this.oldCode = tempOrder.code
            this.client = tempOrder.contract_award.client_id
            this.loadFiles()
          })
          .catch(error => {
          console.log("getOrder -> error", error)
            if (error.response.status === 403 ) {
              this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              });
              this.$router.push({ name: 'home'})
            } else {
              var errorToastMessage = "Ha ocurrido un error cargando la orden de trabajo."
              Util.showErrorToast(this, error, errorToastMessage);
            }
            this.orderList();
        })
      } else {
        this.work_order = {}
        this.client = null
        this.file = null
      }
      this.$v.$reset()
      this.loadContracts()
    },
    loadContracts () {
      this.isLoadingData = true
      this.$store.dispatch('contracts/getContracts')
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de adjudicaciones."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.isLoadingData = false
          this.loadClients()
        })
    },
    loadClients () {
      this.isLoadingData = true
      this.$store.dispatch('clients/getClients')
        .then(() => {})
        .catch(error => {
          this.isLoadingData = false
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la información del cliente."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.isLoadingData = false
          this.loadUsers()
        })
    },
    loadUsers() {
      this.isLoadingData = true
      this.$store.dispatch("users/getAllUsers")
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de operarios."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.authUser = true
          this.isLoadingData = false;
        })
    },
    orderList () {
      this.$router.push({
        name: "order-list"
      });
    },
    save () {
      this.$route.params.id ? this.editOrder() : this.createOrder()
    },
    createOrder () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.loading = true
        this.$store.dispatch('orders/createOrder', this.work_order)
          .then(response => {
            const data = response.data.data      
            const id = data.id      
            console.log("createOrder -> data", data)
            this.$toast("Orden de trabajo creada correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.$router.push({
              name: 'order-edit',
              params: { id: id }
            })
            this.getOrder(id)
            this.step++
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error creando la orden de trabajo."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    editOrder () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.loading = true
        this.$store.dispatch('orders/editOrder', this.work_order)
          .then(() => {
            if (!!this.file && this.fileSizeRule) {
              this.uploadingFileDialog = true
              let upload = {
                id: this.work_order.id,
                fileToUpload: this.file
              }
              this.$store.dispatch('orders/uploadFile', upload)
                .then(() => {
                  this.$toast("Orden de trabajo modificada correctamente.", {
                    color: "success",
                    x: "center",
                    y: "top",
                    icon: "mdi-information"
                  });
                  this.orderList()
                })
                .catch(error => {
                  var errorToastMessage = "Ha ocurrido un error subiendo el archivo."
                  Util.showErrorToast(this, error, errorToastMessage);
                })
                .finally(() => {
                  this.uploadingFileDialog = false
                })
            } else {
              this.$toast("Orden de trabajo modificada correctamente.", {
                color: "success",
                x: "center",
                y: "top",
                icon: "mdi-information"
              });
              this.orderList()
            }
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error modificando la orden de trabajo."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.loading = false
          })
      }
    }
  },
  created () {
    this.getOrder()
  }
}
</script>
